<template>
    <el-dialog :visible.sync="visible" width="1000px" :close-on-click-modal="false" custom-class="note-dialog-component"
        :before-close="handleClose">
        <div slot="title" class="dialog-title">
            <i class="el-icon-edit-outline"></i>
            <span>用户备注</span>
        </div>
        <div class="showDialog">
            <div class="leftInput">
                <div class="input-header">
                    <i class="el-icon-edit"></i>
                    <span>添加备注</span>
                </div>
                <el-input class="dialogInput" type="textarea" :rows="8" placeholder="请输入备注内容..." v-model="textarea"
                    maxlength="500" show-word-limit>
                </el-input>
                <div class="input-footer">
                    <el-button type="primary" @click="addNote" :disabled="!textarea.trim()" class="add-btn">
                        <i class="el-icon-plus"></i>
                        添加备注
                    </el-button>
                </div>
            </div>
            <div class="rightInput">
                <div class="notes-header">
                    <i class="el-icon-document"></i>
                    <span>备注列表 ({{ noteList.length }})</span>
                </div>
                <div class="notes-container">
                    <div v-if="noteList.length === 0" class="empty-notes">
                        <i class="el-icon-document-copy"></i>
                        <p>暂无备注</p>
                        <span>点击左侧添加备注</span>
                    </div>
                    <template v-else>
                        <div v-for="(item, index) in noteList" :key="index" class="noteContentBox">
                            <div class="note-content">
                                <p>{{ item.remarkContent }}</p>
                            </div>
                            <div class="note-footer">
                                <div class="note-time">{{ formatTime(item.createTime) }}</div>
                                <el-button type="text" size="mini" @click="deleteNote(item, index)" class="delete-btn">
                                    <i class="el-icon-delete"></i>
                                </el-button>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </el-dialog>
</template>

<script>
import { addChatNote, delChatNote, chatNoteList } from '@/api/dialogue';

export default {
    name: 'NoteDialog',
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        chatUserInfo: {
            type: Object,
            default: () => ({})
        },
        checkUser: {
            type: Number,
            default: 0
        }
    },
    emits: ['update:visible', 'note-changed'],
    data() {
        return {
            textarea: '',
            noteList: [],
            loading: false
        };
    },
    watch: {
        visible(newVal) {
            if (newVal) {
                this.loadNotes();
            } else {
                this.resetForm();
            }
        }
    },
    methods: {
        // 格式化时间
        formatTime(timestamp) {
            if (!timestamp) return '';
            const date = new Date(timestamp);
            const now = new Date();
            const diff = now - date;

            // 如果是今天
            if (diff < 24 * 60 * 60 * 1000 && date.toDateString() === now.toDateString()) {
                return date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
            }
            // 如果是昨天
            const yesterday = new Date(now);
            yesterday.setDate(yesterday.getDate() - 1);
            if (date.toDateString() === yesterday.toDateString()) {
                return '昨天 ' + date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
            }
            // 其他日期
            return date.toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit' }) + ' ' +
                date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
        },

        // 生成聊天ID
        generateChatId() {
            if (this.checkUser === 1) {
                return this.chatUserInfo.userId + '-' + this.chatUserInfo.platformUserId;
            } else {
                return this.chatUserInfo.platformUserId + '-' + this.chatUserInfo.userId;
            }
        },

        // 加载备注列表
        async loadNotes() {
            try {
                this.loading = true;
                const chatId = this.generateChatId();
                const res = await chatNoteList(chatId);
                if (res.code === 200) {
                    this.noteList = res.data || [];
                } else {
                    this.$message.error(res.msg || '加载备注失败');
                }
            } catch (error) {
                console.error('加载备注失败:', error);
                this.$message.error('加载备注失败');
            } finally {
                this.loading = false;
            }
        },

        // 添加备注
        async addNote() {
            if (!this.textarea.trim()) {
                this.$message.warning('请输入备注内容');
                return;
            }

            try {
                const chatId = this.generateChatId();
                const params = {
                    idUser: chatId,
                    remarkContent: this.textarea.trim()
                };

                const res = await addChatNote(params);
                if (res.code === 200) {
                    this.$message.success('添加备注成功');
                    this.textarea = '';
                    // 重新加载列表，并在加载完成后发射事件
                    await this.loadNotes();
                    // 发射备注变更事件
                    console.log('添加备注成功，发射 note-changed 事件');
                    this.$emit('note-changed');
                } else {
                    this.$message.error(res.msg || '添加备注失败');
                }
            } catch (error) {
                console.error('添加备注失败:', error);
                this.$message.error('添加备注失败');
            }
        },

        // 删除备注
        deleteNote(item, index) {
            this.$confirm('确定要删除这条备注吗？', '提示', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(async () => {
                try {
                    const res = await delChatNote(item.id);
                    if (res.code === 200) {
                        this.$message.success('删除成功');
                        this.noteList.splice(index, 1);
                        // 发射备注变更事件
                        console.log('删除备注成功，发射 note-changed 事件');
                        this.$emit('note-changed');
                    } else {
                        this.$message.error(res.msg || '删除失败');
                    }
                } catch (error) {
                    console.error('删除备注失败:', error);
                    this.$message.error('删除失败');
                }
            }).catch(() => {
                // 用户取消删除
            });
        },

        // 重置表单
        resetForm() {
            this.textarea = '';
        },

        // 关闭弹窗
        handleClose() {
            this.$emit('update:visible', false);
        }
    }
};
</script>

<style lang="scss" scoped>
// 备注弹窗样式 - 使用更具体的选择器避免样式冲突
:deep(.note-dialog-component) {
    // 重置父级样式影响
    border-radius: 8px !important;

    .el-dialog__header {
        padding: 20px !important;
        border-bottom: 1px solid #f0f0f0 !important;
        z-index: 1 !important;
        border-radius: 8px 8px 0 0 !important;

        .dialog-title {
            display: flex;
            align-items: center;
            font-size: 18px;
            font-weight: 600;
            color: #333;

            i {
                margin-right: 8px;
                color: #409eff;
                font-size: 20px;
            }
        }
    }

    .el-dialog__body {
        padding: 0 !important;
        border-radius: 8px !important;
    }
}

.showDialog {
    background-color: #fff;
    width: 100%;
    height: 500px;
    display: flex;
    align-items: stretch;
    border-radius: 0 0 8px 8px;

    .leftInput {
        width: 45%;
        background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
        border-right: 1px solid #e2e8f0;
        display: flex;
        flex-direction: column;
        padding: 24px;
        border-radius: 0 0 0 8px !important;

        .input-header {
            display: flex;
            align-items: center;
            margin-bottom: 16px;
            font-weight: 600;
            color: #374151;

            i {
                margin-right: 8px;
                color: #409eff;
                font-size: 16px;
            }
        }

        .dialogInput {
            flex: 1;
            margin-bottom: 16px;

            :deep(.el-textarea__inner) {
                background: #fff !important;
                border: 1px solid #d1d5db !important;
                border-radius: 8px !important;
                padding: 16px !important;
                font-size: 14px !important;
                line-height: 1.6 !important;
                resize: none !important;
                transition: all 0.3s ease !important;

                &:focus {
                    border-color: #409eff !important;
                    box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.1) !important;
                }

                &::placeholder {
                    color: #9ca3af !important;
                }
            }
        }

        .input-footer {
            display: flex;
            justify-content: flex-end;

            .add-btn {
                background: linear-gradient(135deg, #409eff 0%, #3b82f6 100%) !important;
                border: none !important;
                border-radius: 8px !important;
                padding: 10px 20px !important;
                font-weight: 500 !important;
                transition: all 0.3s ease !important;

                &:hover {
                    transform: translateY(-1px) !important;
                    box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3) !important;
                }

                &:disabled {
                    background: #e5e7eb !important;
                    color: #9ca3af !important;
                    transform: none !important;
                    box-shadow: none !important;
                }

                i {
                    margin-right: 6px;
                }
            }
        }
    }

    .rightInput {
        flex: 1;
        background: #fff;
        display: flex;
        flex-direction: column;
        padding: 16px;
        border-radius: 0 0 8px 0 !important;

        .notes-header {
            display: flex;
            align-items: center;
            margin-bottom: 12px;
            font-weight: 600;
            color: #374151;
            font-size: 14px;

            i {
                margin-right: 6px;
                color: #10b981;
                font-size: 14px;
            }
        }

        .notes-container {
            flex: 1;
            overflow-y: auto;

            &::-webkit-scrollbar {
                width: 6px;
            }

            &::-webkit-scrollbar-track {
                background: #f1f5f9;
                border-radius: 3px;
            }

            &::-webkit-scrollbar-thumb {
                background: #cbd5e1;
                border-radius: 3px;

                &:hover {
                    background: #94a3b8;
                }
            }
        }

        .empty-notes {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 100%;
            color: #9ca3af;

            i {
                font-size: 36px;
                margin-bottom: 12px;
                opacity: 0.5;
            }

            p {
                font-size: 14px;
                font-weight: 500;
                margin-bottom: 6px;
            }

            span {
                font-size: 12px;
            }
        }

        .noteContentBox {
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 6px;
            padding: 12px;
            margin-bottom: 8px;
            transition: all 0.3s ease;

            &:hover {
                background: #f1f5f9;
                border-color: #cbd5e1;
                transform: translateY(-1px);
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            }

            .note-content {
                flex: 1;

                p {
                    margin: 0 0 8px 0;
                    font-size: 13px;
                    line-height: 1.5;
                    color: #374151;
                    word-break: break-word;
                }
            }

            .note-footer {
                display: flex;
                justify-content: space-between;
                align-items: center;

                .note-time {
                    font-size: 11px;
                    color: #6b7280;
                }

                .delete-btn {
                    color: #ef4444 !important;
                    padding: 6px 8px !important;
                    border-radius: 4px !important;
                    transition: all 0.3s ease !important;

                    &:hover {
                        background: #fef2f2 !important;
                        color: #dc2626 !important;
                    }

                    i {
                        font-size: 16px;
                    }
                }
            }
        }
    }
}
</style>
